/**
 * `<caos-page-header>` — the header of a list or overview surface.
 * (Catalog: Views & filtering › PageHeader — the eyebrow, title, description, and count at the head of a surface.)
 *
 * A `breadcrumb` slot sits above an eyebrow (mono label), the page `heading` with an
 * optional record `count` beside it, a `description`, and an `actions` slot pinned to
 * the right. Everything reads a `--caos-*` token; the count reads the neutral text
 * tokens on the page surface (no tint, so the neutral tokens are correct). Styling
 * seams: `::part(header)`, `::part(breadcrumb)`, `::part(eyebrow)`, `::part(title)`,
 * `::part(count)`, `::part(description)`, `::part(actions)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

const sheet = makeSheet(`
  :host { display: block; }

  .header {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-3);
    padding-bottom: var(--caos-space-4);
    border-bottom: 1px solid var(--caos-color-border);
  }

  .breadcrumb { display: block; }
  .breadcrumb:not(:has(*)) { display: none; }

  .bar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--caos-space-4);
    flex-wrap: wrap;
  }
  .lead { display: flex; flex-direction: column; gap: var(--caos-space-1); min-width: 0; flex: 1 1 320px; }

  .eyebrow {
    font-family: var(--caos-font-mono);
    font-size: var(--caos-text-label);
    font-weight: var(--caos-font-weight-label);
    letter-spacing: var(--caos-tracking-wide);
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .eyebrow:empty { display: none; }

  .titleRow { display: flex; align-items: baseline; gap: var(--caos-space-3); flex-wrap: wrap; }
  .title {
    font-family: var(--caos-font-display);
    font-weight: var(--caos-font-weight-display);
    font-size: var(--caos-text-h2);
    line-height: 1.15;
    letter-spacing: var(--caos-tracking-tight);
    color: var(--caos-color-text);
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .count {
    font-family: var(--caos-font);
    font-size: var(--caos-text-body);
    font-weight: 500; /* medium numeric badge — off-scale weight, stays literal */
    color: var(--caos-color-text-muted);
    white-space: nowrap;
  }
  .count:empty { display: none; }

  .description {
    font-size: var(--caos-text-row);
    line-height: 1.55;
    color: var(--caos-color-text-muted);
    max-width: 70ch;
  }
  .description:not(:has(*)):empty { display: none; }

  .actions { flex: none; display: flex; align-items: center; gap: var(--caos-space-2); }
  .actions:not(:has(*)) { display: none; }
`);

export class CaosPageHeader extends CaosElement {
  static get observedAttributes(): string[] {
    return ['eyebrow', 'heading', 'count', 'description'];
  }

  private eyebrowEl: HTMLDivElement;
  private titleEl: HTMLHeadingElement;
  private countEl: HTMLSpanElement;
  private descEl: HTMLDivElement;

  constructor() {
    super(sheet);

    const header = document.createElement('div');
    header.className = 'header';
    header.setAttribute('part', 'header');

    const breadcrumb = document.createElement('div');
    breadcrumb.className = 'breadcrumb';
    breadcrumb.setAttribute('part', 'breadcrumb');
    const bcSlot = document.createElement('slot');
    bcSlot.name = 'breadcrumb';
    breadcrumb.append(bcSlot);

    const bar = document.createElement('div');
    bar.className = 'bar';

    const lead = document.createElement('div');
    lead.className = 'lead';

    this.eyebrowEl = document.createElement('div');
    this.eyebrowEl.className = 'eyebrow';
    this.eyebrowEl.setAttribute('part', 'eyebrow');

    const titleRow = document.createElement('div');
    titleRow.className = 'titleRow';
    this.titleEl = document.createElement('h1');
    this.titleEl.className = 'title';
    this.titleEl.setAttribute('part', 'title');
    this.countEl = document.createElement('span');
    this.countEl.className = 'count';
    this.countEl.setAttribute('part', 'count');
    titleRow.append(this.titleEl, this.countEl);

    this.descEl = document.createElement('div');
    this.descEl.className = 'description';
    this.descEl.setAttribute('part', 'description');
    this.descEl.append(document.createElement('slot')); // default slot = description body

    lead.append(this.eyebrowEl, titleRow, this.descEl);

    const actions = document.createElement('div');
    actions.className = 'actions';
    actions.setAttribute('part', 'actions');
    const actionsSlot = document.createElement('slot');
    actionsSlot.name = 'actions';
    actions.append(actionsSlot);

    bar.append(lead, actions);
    header.append(breadcrumb, bar);
    this.root.append(header);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    this.eyebrowEl.textContent = this.getAttribute('eyebrow') ?? '';
    this.titleEl.textContent = this.getAttribute('heading') ?? '';
    this.countEl.textContent = this.getAttribute('count') ?? '';
    // `description` attribute is a shortcut; the default slot is the richer path.
    const desc = this.getAttribute('description');
    if (desc !== null) this.descEl.textContent = desc;
  }
}

define('caos-page-header', CaosPageHeader);

declare global {
  interface HTMLElementTagNameMap {
    'caos-page-header': CaosPageHeader;
  }
}
